import CountryDropdown from "@/components/giftExperience/CountryDropdown";
import { GFG_LOGO_HIRES } from "@/constants/campaignBuilder";

interface RedemptionHeaderProps {
  /** Partner/company logo shown next to the GfG logo, when provided. */
  partnerLogoUrl?: string;
  partnerLogoAlt?: string;
  /**
   * Float over the content beneath it (the unbox video): absolute, z9 (above the
   * intro overlay), safe-area top inset, no border. Default is an in-flow bar with
   * a bottom border (landing / collection).
   */
  floating?: boolean;
  /**
   * Destination "Shipping to" picker, shown as a segmented pill in the top-right corner
   * (the landing/feed only — the unbox splash omits it). Editable when `locked` is false;
   * `countries` is the selectable shipping allowlist (AVAILABLE_COUNTRIES) — the feed then
   * filters to whatever ships to the chosen country.
   */
  shipTo?: {
    country: string;
    onChange: (code: string) => void;
    locked: boolean;
    countries: { code: string; flag: string; name: string }[];
  };
  /**
   * Pins the logos (GfG and partner share one height) to their phone (50px) or desktop (70px)
   * size. Only the builder's inline preview passes it: its frame is narrower than the browser
   * window, so the `sm` breakpoint would read the window instead of the frame. Omit it
   * (recipient route, fullscreen preview) to follow the viewport.
   */
  isMobile?: boolean;
}

/**
 * Shared recipient-flow header lockup — the GfG logo (50px, 70px from `sm` up, kept identical
 * across every stage) plus an optional partner logo of the same height (width follows its aspect
 * ratio, capped) with a hairline separator, and (on the landing) a top-right "Shipping to"
 * destination picker. Used by the unbox splash (`floating`) and the landing/collection header
 * (in-flow).
 */
export default function RedemptionHeader({
  partnerLogoUrl,
  partnerLogoAlt,
  floating = false,
  shipTo,
  isMobile,
}: RedemptionHeaderProps) {
  return (
    <div
      className="flex items-center justify-center"
      style={
        floating
          ? {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 9,
              gap: 16,
              padding: "calc(14px + env(safe-area-inset-top)) 24px 14px",
              background: "var(--color-white)",
            }
          : {
              // flexWrap: below `md` the "Shipping to" pill is in flow (`ml-auto`) and wraps to
              // its own right-aligned row only when the row overflows (phones).
              position: "relative",
              flexWrap: "wrap",
              gap: 16,
              padding: "14px 24px",
              borderBottom: "1px solid var(--color-card-border)",
              background: "var(--color-white)",
            }
      }
    >
      <img
        src={GFG_LOGO_HIRES}
        alt="Gifts for Good"
        className={`block w-auto object-contain ${logoHeightClass(isMobile)}`}
      />
      {partnerLogoUrl && (
        <>
          <div
            className={separatorHeightClass(isMobile)}
            style={{ width: 1, background: "#cccccc" }}
          />
          <img
            src={partnerLogoUrl}
            alt={partnerLogoAlt ?? "Partner"}
            className={`w-auto object-contain ${logoHeightClass(isMobile)} ${partnerMaxWidthClass(isMobile)}`}
          />
        </>
      )}

      {/* Destination-country picker — segmented pill. From 769px it is absolute top-right so
          the lockup stays centered. Below that it is in flow (`ml-auto`): on phones (<640) it
          wraps to its own right-aligned row when the row overflows; at 640–768 it shares the
          lockup row, right-aligned. */}
      {shipTo && (
        <div className="absolute right-5 top-1/2 flex -translate-y-1/2 items-stretch overflow-hidden rounded-md border border-dark-dust font-lato max-md:static max-md:ml-auto max-md:translate-y-0 max-md:min-h-11.5">
          <span className="flex items-center whitespace-nowrap px-3.5 py-2 text-[13px] font-semibold text-rodeo-black">
            Shipping to
          </span>
          <span aria-hidden="true" className="w-px shrink-0 bg-dark-dust" />
          <CountryDropdown
            compact
            countries={shipTo.countries}
            selectedCountry={shipTo.country}
            onChange={shipTo.onChange}
            locked={shipTo.locked}
          />
        </div>
      )}
    </div>
  );
}

/** Shared logo height (GfG + partner): the viewport breakpoint, unless pinned (see `isMobile`). */
function logoHeightClass(isMobile: boolean | undefined): string {
  if (isMobile === undefined) return "h-12.5 sm:h-17.5";
  return isMobile ? "h-12.5" : "h-17.5";
}

/**
 * Partner logo width cap. Phones: 132px; 220px from `sm`. The wider cap is safe because the
 * in-flow "Shipping to" pill (below 769px) only wraps to its own row when the row overflows, so
 * it never overlaps the lockup. From 769px it is absolute at right-5 and clears a 220px partner
 * (next to the 2:1 GfG logo, 140x70) as before.
 */
function partnerMaxWidthClass(isMobile: boolean | undefined): string {
  if (isMobile === undefined) return "max-w-33 sm:max-w-55";
  return isMobile ? "max-w-33" : "max-w-55";
}

/** Separator height (36px phone, 50px desktop), following the same pinning as the logos. */
function separatorHeightClass(isMobile: boolean | undefined): string {
  if (isMobile === undefined) return "h-9 sm:h-12.5";
  return isMobile ? "h-9" : "h-12.5";
}
